// Modal POWIAZANIA pozycji oferty ze wspoldzielona kalkulacja (flow "Powiaz kalkulacje").
// Reusuje endpoint listy szablonow (GET calculation-templates) - identyczna sekcja
// "Dla tego produktu" + "Inne kalkulacje". Roznica vs Klonowanie: zamiast tworzyc kopie,
// ustawia linkedCalculationId na pozycji - powiazana kalkulacja jest WSPOLDZIELONA
// (zmiany w niej dotykaja wszystkich pozycji ktore na nia wskazuja).
//
// Konflikt: jesli pozycja ma juz wlasna kalkulacje, backend rzuca blad z translacja
// `sales_crm.offer_version_calculation.link.position_has_own`. UI pokazuje confirm
// "zastapic?" i jesli tak - retry z force=true (kalkulacja wlasna zostanie usunieta).

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';

import { WiseModal } from 'components/wise';
import {
  useGetSalesCrmCalculationTemplates,
  usePostSalesCrmOfferCalculationLink,
  type ICalculationTemplate,
  type ICalculationTemplatePosition
} from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/ChooseCalculationTemplateModal.module.scss';

interface ILinkCalculationModalProps {
  open: boolean;
  onClose: () => void;
  /** Callback po pomyslnym powiazaniu - dostaje id i nazwe kalkulacji (NIE nowej - tej samej). */
  onLinkedSuccess: (linkedCalculation: { id: number; name: string }) => void;
  clientId: number;
  offerId: string;
  positionId: number;
  productName: string;
}

const formatPrice = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

const formatDate = (raw: string | null | undefined): string => {
  if (!raw) return '-';
  try {
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(raw));
  } catch {
    return raw;
  }
};

const priceTypeLabel = (type: 'UNIT' | 'TOTAL' | null): string =>
  type === 'UNIT' ? '/ szt.' : 'jednorazowo';

const TemplateTooltipBody: FC<{ positions: ICalculationTemplatePosition[] }> = ({ positions }) => (
  <div style={{ minWidth: 240 }}>
    {positions.length === 0 ? (
      <em>Brak składników</em>
    ) : (
      positions.map((p, idx) => (
        <div
          key={idx}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <span>
            <strong>{p.name}</strong>{' '}
            <span style={{ opacity: 0.7, fontSize: 11 }}>{priceTypeLabel(p.priceType)}</span>
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatPrice(p.priceNet)}
            {p.costNet != null && (
              <span style={{ opacity: 0.7, fontSize: 11 }}> (koszt {formatPrice(p.costNet)})</span>
            )}
          </span>
        </div>
      ))
    )}
  </div>
);

const LinkCalculationModal: FC<ILinkCalculationModalProps> = ({
  open,
  onClose,
  onLinkedSuccess,
  clientId,
  offerId,
  positionId,
  productName
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [searchInput, setSearchInput] = useState('');
  const [searchActive, setSearchActive] = useState<string | undefined>(undefined);
  /** Cache wybranej kalkulacji - jesli backend zwroci konflikt, retry zachowa target. */
  const [pendingSelection, setPendingSelection] = useState<ICalculationTemplate | null>(null);

  const { data, isLoading, isError } = useGetSalesCrmCalculationTemplates(
    clientId,
    offerId,
    positionId,
    { search: searchActive, limit: 50 },
    { enabled: open && positionId > 0 }
  );

  const { mutate: linkCalculation, isLoading: isLinking } = usePostSalesCrmOfferCalculationLink(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        if (!pendingSelection) return;
        enqueueSnackbar(t('salesCrm.calculation.link.success'), { variant: 'success' });
        onLinkedSuccess({ id: pendingSelection.id, name: pendingSelection.name ?? '' });
        setPendingSelection(null);
      },
      onError: (error) => {
        const translationKey =
          (error as { translation?: string } | undefined)?.translation
          ?? (error as { message?: string } | undefined)?.message
          ?? '';
        // Konflikt: pozycja ma wlasna kalkulacje. Pytamy o force.
        if (
          typeof translationKey === 'string'
          && translationKey.includes('position_has_own')
          && pendingSelection
        ) {
          // eslint-disable-next-line no-alert
          const accept = window.confirm(t('salesCrm.calculation.link.confirmReplace'));
          if (accept) {
            linkCalculation({ calculationId: pendingSelection.id, force: true });
            return;
          }
          setPendingSelection(null);
          return;
        }
        enqueueSnackbar(t('salesCrm.calculation.link.error'), { variant: 'error' });
        setPendingSelection(null);
      }
    }
  );

  const related = data?.relatedToProduct ?? [];
  const other = data?.other ?? [];

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchActive(trimmed === '' ? undefined : trimmed);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchActive(undefined);
  };

  const handleSelectTemplate = (template: ICalculationTemplate) => {
    if (isLinking) return;
    setPendingSelection(template);
    linkCalculation({ calculationId: template.id, force: false });
  };

  const renderTemplateRow = (template: ICalculationTemplate) => (
    <div key={template.id} className={styles.templateRow}>
      <div className={styles.templateMain}>
        <div className={styles.templateNameLine}>
          <span className={styles.templateName}>{template.name}</span>
          {template.isSameClient && (
            <span className={styles.sameClientBadge}>
              {t('salesCrm.calculation.template.sameClientBadge')}
            </span>
          )}
          {template.positions.length > 0 && (
            <Tooltip
              title={<TemplateTooltipBody positions={template.positions} />}
              arrow
              placement="top">
              <InfoOutlinedIcon fontSize="small" className={styles.infoIcon} />
            </Tooltip>
          )}
        </div>
        <div className={styles.templateMeta}>
          <span>
            <strong>{t('salesCrm.calculation.template.client')}:</strong>{' '}
            {template.clientName ?? '-'}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.offer')}:</strong>{' '}
            {template.offerNumber ?? '-'}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.createdAt')}:</strong>{' '}
            {formatDate(template.createdAt)}
          </span>
          <span>
            <strong>{t('salesCrm.calculation.template.valueNet')}:</strong>{' '}
            {formatPrice(template.valueNet)}
          </span>
        </div>
      </div>
      <div className={styles.templateActions}>
        <Button
          variant="contained"
          size="small"
          disabled={isLinking}
          onClick={() => handleSelectTemplate(template)}
          className={styles.primaryButton}>
          {t('salesCrm.calculation.link.select')}
        </Button>
      </div>
    </div>
  );

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t('salesCrm.calculation.link.title', { product: productName })}
      maxWidth={760}>
      <div className={styles.body}>
        <div className={styles.searchRow}>
          <TextField
            size="small"
            fullWidth
            value={searchInput}
            placeholder={t('salesCrm.calculation.template.searchPlaceholder')}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" style={{ marginRight: 6, color: '#999' }} />
              )
            }}
          />
          <Button onClick={handleSearch} variant="outlined" size="small">
            {t('salesCrm.calculation.template.searchCta')}
          </Button>
          {searchActive && (
            <Button onClick={handleClearSearch} variant="text" size="small">
              {t('salesCrm.calculation.template.clearSearch')}
            </Button>
          )}
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && isError && (
          <div className={styles.error}>{t('salesCrm.calculation.template.loadError')}</div>
        )}

        {!isLoading && !isError && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                {t('salesCrm.calculation.template.sectionRelated', { product: productName })}
              </div>
              {related.length === 0 ? (
                <div className={styles.empty}>
                  {searchActive
                    ? t('salesCrm.calculation.template.noRelatedMatchSearch')
                    : t('salesCrm.calculation.template.noRelated')}
                </div>
              ) : (
                related.map(renderTemplateRow)
              )}
            </div>

            {searchActive && other.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  {t('salesCrm.calculation.template.sectionOther')}
                </div>
                {other.map(renderTemplateRow)}
              </div>
            )}

            {searchActive && other.length === 0 && related.length === 0 && (
              <div className={styles.empty}>{t('salesCrm.calculation.template.noResults')}</div>
            )}
          </>
        )}

        <div className={styles.footer}>
          <Button onClick={onClose} variant="text" disabled={isLinking}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default LinkCalculationModal;
